import { createApi } from "@reduxjs/toolkit/query/react";
import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";
import type { IApiGeneralParams, IApiPaginatedResponse, IApiResponse } from "@/types";

import type { IAnnouncementResponse, ISingleAnnouncementResponse } from "@/types/announcement.interface";
import {
  API_BULK_DELETE_ANNOUNCEMENTS,
  API_DELETE_ANNOUNCEMENT,
  API_GET_ANNOUNCEMENT_BY_ID,
  API_GET_ANNOUNCEMENTS,
  API_UPDATE_ALL_ANNOUNCEMENTS_AS_READ,
  API_UPDATE_ANNOUNCEMENT_AS_READ,
  API_UPDATE_MULTIPLE_ANNOUNCEMENTS_AS_READ,
} from "@/constants/apiEndpoints";
import { setUnreadAnnouncementCount } from "@/store/slices/user.slice";

export const announcementApi = createApi({
  reducerPath: "announcementApi",
  baseQuery: dynamicBaseQuery,
  refetchOnFocus: false,
  tagTypes: ["getAllAnnouncements", "getAnnouncement", "getLatestAnnouncements"],
  endpoints: (builder) => ({
    getAllAnnouncements: builder.query<
      IApiPaginatedResponse<IAnnouncementResponse>,
      IApiGeneralParams & { platformSectionType: string }
    >({
      query: (params) => ({
        url: API_GET_ANNOUNCEMENTS,
        method: "GET",
        params,
      }),
      providesTags: ["getAllAnnouncements"],
      onQueryStarted: async (_body, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          const { unreadCount } = data.data;
          dispatch(setUnreadAnnouncementCount(unreadCount));
        } catch (e: any) {
          console.error(e?.message ?? e?.data?.message ?? "Something went wrong. Please try again later.");
        }
      },
    }),
    getLatestAnnouncements: builder.query<IApiPaginatedResponse<IAnnouncementResponse>, void>({
      query: () => ({
        url: API_GET_ANNOUNCEMENTS,
        method: "GET",
        params: {
          limit: 10,
          platformSectionType: "ALL",
          page: 1,
        },
      }),
      providesTags: ["getLatestAnnouncements"],
    }),
    getAnnouncementById: builder.query<IApiResponse<ISingleAnnouncementResponse>, number>({
      query: (announcementId) => ({
        url: API_GET_ANNOUNCEMENT_BY_ID(announcementId),
        method: "GET",
      }),

      providesTags: (_result, _error, id) => [{ type: "getAnnouncement", id }],

      onQueryStarted: async (announcementId, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;

          const announcement = data?.data?.announcementData;

          if (announcement && !announcement.isRead) {
            dispatch(announcementApi.endpoints.updateSingleAnnouncementAsRead.initiate(announcementId));
          }
        } catch (error) {
          console.error("Failed to fetch announcement", error);
        }
      },
    }),
    updateSingleAnnouncementAsRead: builder.mutation<IApiPaginatedResponse<void>, number>({
      query: (announcementId) => {
        return {
          url: API_UPDATE_ANNOUNCEMENT_AS_READ(announcementId),
          method: "PATCH",
        };
      },
      invalidatesTags: ["getAllAnnouncements", "getAnnouncement"],
    }),
    updateMultipleAnnouncementAsRead: builder.mutation<IApiPaginatedResponse<void>, { ids: [] }>({
      query: (body) => {
        return {
          url: API_UPDATE_MULTIPLE_ANNOUNCEMENTS_AS_READ,
          method: "PATCH",
          body,
        };
      },
      invalidatesTags: ["getAllAnnouncements", "getAnnouncement"],
    }),
    updateAllAnnouncementAsRead: builder.mutation<IApiPaginatedResponse<void>, void>({
      query: () => {
        return {
          url: API_UPDATE_ALL_ANNOUNCEMENTS_AS_READ,
          method: "PATCH",
        };
      },
      invalidatesTags: ["getAllAnnouncements", "getAnnouncement"],
    }),
    deleteAnnouncementById: builder.mutation<IApiPaginatedResponse<void>, { announcementId: number }>({
      query: ({ announcementId }) => ({
        url: API_DELETE_ANNOUNCEMENT(announcementId),
        method: "DELETE",
      }),
      invalidatesTags: ["getAllAnnouncements", "getAnnouncement"],
    }),
    deleteMultipleAnnouncement: builder.mutation<IApiPaginatedResponse<void>, { ids: [] }>({
      query: (body) => ({
        url: API_BULK_DELETE_ANNOUNCEMENTS,
        method: "POST",
        body,
      }),
      invalidatesTags: ["getAllAnnouncements", "getAnnouncement"],
    }),
    getAnnouncement: builder.infiniteQuery<
      IApiPaginatedResponse<IAnnouncementResponse>,
      { limit: number; platformSectionType: string },
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (result) => {
          if (!result?.meta) return undefined;
          const { currentPage, totalPages } = result.meta;
          return currentPage < totalPages ? currentPage + 1 : undefined;
        },
      },
      query: ({ queryArg, pageParam }) => ({
        url: API_GET_ANNOUNCEMENTS,
        method: "GET",
        params: {
          limit: queryArg.limit,
          platformSectionType: queryArg.platformSectionType,
          page: pageParam,
        },
      }),
      providesTags: ["getAnnouncement"],
      onQueryStarted: async (_body, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          const { unreadCount } = data.pages[0].data;
          dispatch(setUnreadAnnouncementCount(unreadCount));
        } catch (e: any) {
          console.error(e?.message ?? e?.data?.message ?? "Something went wrong. Please try again later.");
        }
      },
    }),
  }),
});

export const {
  useGetAllAnnouncementsQuery,
  useUpdateAllAnnouncementAsReadMutation,
  useUpdateMultipleAnnouncementAsReadMutation,
  useUpdateSingleAnnouncementAsReadMutation,
  useDeleteMultipleAnnouncementMutation,
  useDeleteAnnouncementByIdMutation,
  useGetAnnouncementInfiniteQuery,
  useGetAnnouncementByIdQuery,
  useGetLatestAnnouncementsQuery,
} = announcementApi;
